import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "./CompanyContext";

type DesignSystemContextValue = {
  themeKey: string;
  source: "default" | "root" | "tenant" | "user";
  isLoading: boolean;
};

const DesignSystemContext = createContext<DesignSystemContextValue | undefined>(
  undefined
);

const THEME_CLASS_PREFIX = "theme-";

const applyThemeClass = (themeKey: string) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  // Remove previous cultural/theme classes
  Array.from(root.classList)
    .filter(cls => cls.startsWith(THEME_CLASS_PREFIX))
    .forEach(cls => root.classList.remove(cls));

  // Handle dark separately (since Tailwind uses `.dark`)
  if (themeKey === "dark") {
    root.classList.add("dark");
    return;
  } else {
    root.classList.remove("dark");
  }

  if (themeKey && themeKey !== "light") {
    root.classList.add(`${THEME_CLASS_PREFIX}${themeKey}`);
  }
};

export function DesignSystemProvider({ children }: { children: ReactNode }) {
  const { selectedCompanyId, isLoading: companiesLoading } = useCompany();
  const [activeTheme, setActiveTheme] = useState<string>("light");

  const { data, isLoading } = trpc.designPreferences.resolve.useQuery(
    { companyId: selectedCompanyId },
    { enabled: !companiesLoading }
  );

  const themeKey = data?.resolvedThemeKey ?? "light";
  const source = data?.source ?? "default";

  useEffect(() => {
    if (themeKey) {
      setActiveTheme(themeKey);
      applyThemeClass(themeKey);
    }
  }, [themeKey]);

  const value = useMemo<DesignSystemContextValue>(
    () => ({
      themeKey: activeTheme,
      source,
      isLoading: isLoading || companiesLoading,
    }),
    [activeTheme, source, isLoading, companiesLoading]
  );

  return (
    <DesignSystemContext.Provider value={value}>
      {children}
    </DesignSystemContext.Provider>
  );
}

export function useDesignSystem() {
  const ctx = useContext(DesignSystemContext);
  if (!ctx) {
    throw new Error(
      "useDesignSystem must be used within a DesignSystemProvider"
    );
  }
  return ctx;
}
